/**
 * "Verilerden bölge öner" — `GET /api/regions/suggestions` ve `POST /api/regions/suggestions/apply`.
 * Sunucu sözleşmesi `apps/api/src/regions/region-suggestions.ts`; uygulama yalnız Region yazar.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

export interface RegionSuggestion {
  name: string;
  /** Küçük harfli (tr) mahalle adları. */
  neighborhoods: string[];
  orderCount: number;
  /** Baskın etiketin payı, tam sayı yüzde. */
  confidence: number;
  description: string;
  quadrant: 'NW' | 'NE' | 'SW' | 'SE';
  /** Baskın etiketinin kadranı farklı olan mahalleler (geojson yazımı). */
  border: string[];
  /** Ön-dolu araç (null = eşleme yok). Kullanıcı panelde değiştirebilir. */
  suggestedVehicleId: string | null;
  vehicleHint: 'ABANT_LABEL' | 'HAYAT_LABEL' | null;
  vehicleWarning: 'NO_MAPPING' | 'AMBIGUOUS' | null;
  /** Taslak rengi (#rrggbb). */
  color: string;
}

/** Kullanıcının onayladığı taslak: seçilen araç ve renk (araç yoksa null). */
export interface SuggestionChoice {
  name: string;
  neighborhoods: string[];
  defaultVehicleId: string | null;
  color: string;
}

/** Yalnız panel açıkken okunur (`enabled`): her ekran açılışında 30 günlük tarama yapılmasın. */
export function useRegionSuggestions(enabled: boolean) {
  return useQuery({
    queryKey: ['region-suggestions'],
    queryFn: () =>
      api.get<{ drafts: RegionSuggestion[]; unplaced: string[]; scanned: number }>(
        '/api/regions/suggestions',
      ),
    enabled,
    staleTime: 0,
  });
}

export function useApplyRegionSuggestions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (choices: SuggestionChoice[]) =>
      api.post<{ created: { id: string; name: string }[] }>('/api/regions/suggestions/apply', {
        // Araç yoksa alan hiç gönderilmez: sunucu `defaultVehicleId` için boş metni kabul etmez.
        drafts: choices.map((c) => ({
          name: c.name,
          neighborhoods: c.neighborhoods,
          color: c.color,
          ...(c.defaultVehicleId ? { defaultVehicleId: c.defaultVehicleId } : {}),
        })),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['regions'] });
      void qc.invalidateQueries({ queryKey: ['region-suggestions'] });
    },
  });
}
